import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Boxes, Check, CheckCheck, CheckCircle2, ChevronDown, Clock, Hourglass, Package, PlugZap, RefreshCw, RotateCcw, ShieldCheck, ShoppingCart, Truck } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, Skeleton } from "@/components/ui";
import { api, canManage, type ActionArea, type ActionItem, type ActionItems, type ActionSeverity } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";

import { t, msg } from "@/lib/i18n";
export const AREA_META: Record<ActionArea, { label: string; Icon: typeof Package }> = {
  connections: { label: msg("Verbindungen"), Icon: PlugZap },
  orders: { label: msg("Aufträge"), Icon: ShoppingCart },
  products: { label: msg("Artikel"), Icon: Package },
  stock: { label: msg("Bestand"), Icon: Boxes },
  inbounds: { label: msg("Wareneingänge"), Icon: Truck },
  returns: { label: msg("Retouren"), Icon: RotateCcw },
  account: { label: msg("Konto"), Icon: ShieldCheck },
};
const SEVERITY_META: Record<ActionSeverity, { label: string; bar: string; dot: string; tone: "danger" | "warning" | "info" }> = {
  critical: { label: msg("Dringend"), bar: "bg-danger", dot: "bg-danger", tone: "danger" },
  warning: { label: msg("Wichtig"), bar: "bg-warning", dot: "bg-warning", tone: "warning" },
  info: { label: msg("Hinweis"), bar: "bg-info", dot: "bg-info", tone: "info" },
};

/** gemeinsame Abfrage für Seite, Dashboard und Zahl in der Navigation (eine Anfrage, minütlich aktualisiert) */
export function useActionItems() {
  const { tenantId } = useTenant();
  return useQuery({ queryKey: ["action-items", tenantId], queryFn: () => api.actionItems.get(tenantId), refetchInterval: 60_000, staleTime: 15_000 });
}

/** „Erledigt“ mit sofortiger Wirkung (optimistisch) und „Rückgängig“ */
function useAck() {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const key = ["action-items", tenantId];
  const m = useMutation({
    mutationFn: ({ keys, undo }: { keys: string[]; undo?: boolean }) => api.actionItems.ack(tenantId, keys, undo),
    onMutate: async ({ keys, undo }) => {
      if (undo) return;
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<ActionItems>(key);
      if (prev) {
        const drop = new Set(keys);
        const items = prev.items
          .map((i) => (i.ackable ? { ...i, entries: i.entries.filter((e) => !drop.has(e.key)), count: i.count - i.entries.filter((e) => drop.has(e.key)).length } : i))
          .filter((i) => i.count > 0);
        qc.setQueryData<ActionItems>(key, { ...prev, items });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast.error("Konnte nicht gespeichert werden – bitte erneut versuchen.");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
  return (keys: string[], what: string) =>
    m.mutate({ keys }, { onSuccess: () => toast.success(what, { action: { label: msg("Rückgängig"), onClick: () => m.mutate({ keys, undo: true }) }, duration: 8000 }) });
}

export function ActionItemsPage() {
  const { role } = useTenant();
  const q = useActionItems();
  // auf Seitenebene: Karten verschwinden nach „Erledigt“, der Hinweis mit „Rückgängig“ muss trotzdem erscheinen
  const ack = useAck();
  const [params, setParams] = useSearchParams();
  const area = params.get("area") as ActionArea | null;
  const sev = params.get("severity") as ActionSeverity | null;
  const data = q.data;
  const items = (data?.items ?? []).filter((i) => (!area || i.area === area) && (!sev || i.severity === sev));
  const areas = [...new Set((data?.items ?? []).map((i) => i.area))];
  const set = (k: string, v: string | null) => setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });

  return (
    <div className="pb-16">
      <PageHeader
        title={t("Handlungsbedarf")}
        description={t("Alles, was gerade eure Aufmerksamkeit braucht – das Dringendste zuerst. Erledigtes verschwindet von selbst.")}
        actions={
          <Button variant="ghost" size="sm" icon={<RefreshCw className={cx("size-3.5", q.isFetching && "animate-spin")} />} onClick={() => void q.refetch()}>
            {data ? t("Stand {v}", { v: fmtRelative(data.generatedAt) }) : t("Aktualisieren")}
          </Button>
        }
      />
      <div className="px-4 md:px-8 space-y-5 max-w-5xl">
        {/* Filter: Dringlichkeit und Bereich */}
        {data && data.items.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {(["critical", "warning", "info"] as const).map((s) => {
              const n = data.items.filter((i) => i.severity === s).length;
              if (!n) return null;
              const active = sev === s;
              return (
                <button key={s} onClick={() => set("severity", active ? null : s)} className={cx("inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[12.5px] font-medium transition-colors", active ? "bg-ink text-ink-fg border-ink" : "bg-surface border-border hover:bg-surface-2")}>
                  <span className={cx("size-2 rounded-full", SEVERITY_META[s].dot)} /> {t(SEVERITY_META[s].label)} <span className="tabular-nums opacity-70">{n}</span>
                </button>
              );
            })}
            {areas.length > 1 && <span className="mx-1 h-4 w-px bg-border" />}
            {areas.length > 1 &&
              areas.map((a) => {
                const { label, Icon } = AREA_META[a];
                const active = area === a;
                return (
                  <button key={a} onClick={() => set("area", active ? null : a)} className={cx("inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[12.5px] font-medium transition-colors", active ? "bg-ink text-ink-fg border-ink" : "bg-surface border-border hover:bg-surface-2 text-fg-muted")}>
                    <Icon className="size-3.5" /> {t(label)}
                  </button>
                );
              })}
          </div>
        )}

        {q.isLoading && (
          <div className="space-y-3">
            {Array.from({ length: 4 }, (_, i) => (
              <Card key={i} className="p-5"><Skeleton className="h-4 w-1/2" /><Skeleton className="h-3 w-3/4 mt-2" /></Card>
            ))}
          </div>
        )}
        {q.isError && (
          <Card className="p-5 text-[13px]">
            {t("Handlungsbedarf konnte nicht geladen werden.")} <button className="underline" onClick={() => void q.refetch()}>{t("Erneut versuchen")}</button>
          </Card>
        )}

        {data && data.items.length === 0 && (
          <Card className="px-6 py-14 text-center">
            <CheckCircle2 className="size-8 text-success mx-auto" />
            <p className="mt-3 text-[16px] font-semibold">{t("Alles erledigt")}</p>
            <p className="mt-1 text-fg-muted text-[13px]">{t("Gerade gibt es nichts zu tun. Sobald etwas eure Aufmerksamkeit braucht, steht es hier.")}</p>
          </Card>
        )}

        <div className="space-y-3">
          {items.map((it, i) => (
            <ActionCard key={it.key} item={it} defaultOpen={i < 3 && it.entries.length > 0} canManage={canManage(role)} ack={ack} />
          ))}
        </div>

        {data && data.waiting.length > 0 && <WaitingSection items={data.waiting} />}
      </div>
    </div>
  );
}

function ActionCard({ item, defaultOpen, canManage: mayManage, ack }: { item: ActionItem; defaultOpen: boolean; canManage: boolean; ack: (keys: string[], what: string) => void }) {
  const [open, setOpen] = useState(defaultOpen);
  const { Icon } = AREA_META[item.area];
  const s = SEVERITY_META[item.severity];
  const blocked = item.manage && !mayManage;
  const more = item.count - item.entries.length;
  return (
    <Card className="relative overflow-hidden">
      <span className={cx("absolute left-0 top-0 bottom-0 w-1", s.bar)} />
      <div className="pl-5 pr-4 py-4 flex flex-wrap md:flex-nowrap items-start gap-3">
        <span className="size-8 shrink-0 rounded-lg bg-surface-2 grid place-items-center text-fg-muted"><Icon className="size-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold text-[14px]">{t(item.title)}</h2>
            <Badge tone={s.tone}>{t(s.label)}</Badge>
            {item.since && <span className="inline-flex items-center gap-1 text-[12px] text-fg-subtle"><Clock className="size-3" /> {t("seit")} {fmtRelative(item.since).replace(/^vor /, "")}</span>}
          </div>
          <p className="mt-0.5 text-[13px] text-fg-muted">{t(item.detail)}</p>
          {item.entries.length > 0 && (
            <button onClick={() => setOpen((o) => !o)} className="mt-2 inline-flex items-center gap-1 text-[12.5px] font-medium text-fg-muted hover:text-fg" aria-expanded={open}>
              <ChevronDown className={cx("size-3.5 transition-transform", open && "rotate-180")} />
              {open ? t("Ausblenden") : t("Betroffene anzeigen ({v})", { v: fmtNumber(item.count) })}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
          {item.ackable && item.entries.length > 1 && (
            <Button size="sm" variant="ghost" icon={<CheckCheck className="size-3.5" />} onClick={() => ack(item.entries.map((e) => e.key), `${item.entries.length} als erledigt markiert`)}>
              {t("Alle erledigt")}
            </Button>
          )}
          {blocked ? (
            <span className="text-[12px] text-fg-subtle">{t("Nur Administratoren")}</span>
          ) : (
            <Link to={item.cta.to} className="ml-auto md:ml-0">
              <Button size="sm" variant={item.severity === "critical" ? "primary" : "secondary"}>
                {t(item.cta.label)} <ArrowRight className="size-3.5" />
              </Button>
            </Link>
          )}
        </div>
      </div>
      {open && item.entries.length > 0 && (
        <ul className="border-t border-border divide-y divide-border">
          {item.entries.map((e) => (
            <li key={e.key} className="group flex items-center gap-3 pl-5 md:pl-16 pr-4 py-2 hover:bg-surface-2/50">
              <Link to={e.to} className="min-w-0 flex-1 flex items-center gap-3">
                <span className="font-mono text-[12.5px] font-medium shrink-0">{t(e.label)}</span>
                {e.detail && <span className="text-[12.5px] text-fg-muted truncate">{t(e.detail)}</span>}
              </Link>
              {e.at && <span className="hidden sm:block text-[11.5px] text-fg-subtle shrink-0">{fmtRelative(e.at)}</span>}
              {item.ackable ? (
                <Button size="sm" variant="ghost" icon={<Check className="size-3.5" />} onClick={() => ack([e.key], `${e.label} erledigt`)} aria-label={t("{label} als erledigt markieren", { label: e.label })}>
                  {t("Erledigt")}
                </Button>
              ) : (
                <Link to={e.to} aria-label={t("{label} öffnen", { label: e.label })} className="text-fg-subtle group-hover:text-fg"><ArrowRight className="size-3.5" /></Link>
              )}
            </li>
          ))}
          {more > 0 && (
            <li className="pl-5 md:pl-16 pr-4 py-2 text-[12.5px]">
              <Link to={item.cta.to} className="text-fg-muted hover:text-fg">+ {fmtNumber(more)} {t("weitere ansehen")}</Link>
            </li>
          )}
        </ul>
      )}
    </Card>
  );
}

function WaitingSection({ items }: { items: ActionItem[] }) {
  const [open, setOpen] = useState(false);
  const total = items.reduce((n, i) => n + i.count, 0);
  return (
    <div className="pt-2">
      <button onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-2 text-[13px] font-medium text-fg-muted hover:text-fg" aria-expanded={open}>
        <Hourglass className="size-4" /> {t("Läuft bei Logiful oder wartet – nichts zu tun (")}{fmtNumber(total)})
        <ChevronDown className={cx("size-3.5 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <ul className="mt-3 space-y-2">
          {items.map((i) => (
            <li key={i.key}>
              <Link to={i.cta.to} className="flex items-center gap-3 rounded-xl border border-dashed border-border px-4 py-3 hover:bg-surface-2/50">
                <span className="min-w-0">
                  <span className="block text-[13px] font-medium">{t(i.title)}</span>
                  <span className="block text-[12.5px] text-fg-muted">{t(i.detail)}</span>
                </span>
                <ArrowRight className="ml-auto size-3.5 text-fg-subtle shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Dashboard: die wichtigsten Punkte kompakt, Link zur vollständigen Liste */
export function ActionSummary({ limit = 6 }: { limit?: number }) {
  const q = useActionItems();
  const items = q.data?.items ?? [];
  return (
    <Card>
      <div className="px-5 pt-4 pb-3 flex items-center">
        <h2 className="font-semibold">{t("Handlungsbedarf")}</h2>
        <span className="ml-2 text-fg-subtle text-[12.5px]">{t("das Dringendste zuerst")}</span>
        {items.length > 0 && <Link to="/todo" className="ml-auto text-[12.5px] text-fg-muted hover:text-fg">{t("Alle (")}{items.length})</Link>}
      </div>
      <ul className="divide-y divide-border">
        {q.isLoading && Array.from({ length: 4 }, (_, i) => <li key={i} className="px-5 py-3.5"><Skeleton className="h-4 w-2/3" /></li>)}
        {items.slice(0, limit).map((i) => {
          const { Icon } = AREA_META[i.area];
          return (
            <li key={i.key}>
              <Link to={i.cta.to} className="group flex items-center gap-3 px-5 py-3 hover:bg-surface-2/60 transition-colors">
                <span className={cx("size-2 rounded-full shrink-0", SEVERITY_META[i.severity].dot)} />
                <Icon className="size-4 text-fg-subtle shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-[13px]">{t(i.title)}</p>
                  <p className="text-[12px] text-fg-muted truncate">{t(i.detail)}</p>
                </div>
                <span className="ml-auto shrink-0 inline-flex items-center gap-1 text-[12.5px] font-medium text-fg-muted group-hover:text-fg">
                  {t(i.cta.label)} <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            </li>
          );
        })}
        {items.length > limit && (
          <li><Link to="/todo" className="block px-5 py-2.5 text-[12.5px] text-fg-muted hover:text-fg">+ {items.length - limit} {t("weitere Punkte")}</Link></li>
        )}
        {q.data && items.length === 0 && (
          <li className="px-5 py-8 text-center text-fg-muted flex flex-col items-center gap-2">
            <CheckCircle2 className="size-6 text-success" /> {t("Alles erledigt. Gute Arbeit!")}
          </li>
        )}
      </ul>
    </Card>
  );
}
